import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  useGetIdentity,
  useListContext,
  useTranslate,
  type Exporter,
} from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";

import type { Company, Sale } from "../types";
import { DataImportButton } from "../dataImport/DataImportButton";
import { TopToolbar } from "../layout/TopToolbar";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyListFilter } from "./CompanyListFilter";
import { ImageList } from "./GridList";

const companyExporter: Exporter<Company> = async (
  records,
  fetchRelatedRecords,
) => {
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");

  const companies = records.map((company) => {
    return {
      ...company,
      sales:
        company.sales_id != null
          ? `${sales[company.sales_id]?.first_name ?? ""} ${sales[company.sales_id]?.last_name ?? ""}`.trim()
          : undefined,
      revistas: Array.isArray(company.revistas)
        ? company.revistas.join(", ")
        : company.revistas,
      ramos: Array.isArray(company.ramos)
        ? company.ramos.join(", ")
        : company.ramos,
      context_links: Array.isArray(company.context_links)
        ? company.context_links.join(", ")
        : company.context_links,
    };
  });
  return jsonExport(companies, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "companies");
  });
};

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      exporter={companyExporter}
      actions={<CompanyListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const isMobile = useIsMobile();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div className={`w-full flex ${isMobile ? "flex-col" : "flex-row"} gap-8`}>
      <CompanyListFilter />
      <div className="flex flex-col flex-1 gap-4">
        <ImageList />
      </div>
    </div>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <SortButton fields={["name", "created_at", "nb_contacts"]} />
      <DataImportButton resource="companies" />
      <ExportButton exporter={companyExporter} />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};
